/**
 * The field errors of a form that submits on a button — a dialog, a create
 * page (spec I-2813 §4): which input shows which field error of the last
 * refused submit. Autosave inputs reset themselves; here the screen does it —
 * a field's error leaves when that field gets a new value (G3), all of them
 * before the next submit.
 */
import { shallowRef } from 'vue';
import type { FieldErrorRecord } from './problemHelpers';

/**
 * Where `reportWriteFailure` puts the field errors that bind to an input
 * (`fields`): the keys the surface shows, the record they belong to (binds a
 * `target`) and how to show the texts — key → the field error's sentence.
 */
export interface FieldErrorsTarget {
  readonly keys: readonly string[];
  readonly record?: FieldErrorRecord;
  show(texts: ReadonlyMap<string, string>): void;
}

/**
 * The field errors of a form, usable as `fields` of `reportWriteFailure`,
 * which is the one way a refusal reaches them: it shows the bound field
 * errors here and lists the unbound ones in the summary (K16). The lock
 * after `saved_then_failed` is the summary's `locked` (K18), not a state of
 * the form.
 */
export interface FieldErrors extends FieldErrorsTarget {
  /** The sentence the input of key shows (`NxLoadingWrapper :error-message`). */
  errorFor(key: string): string | undefined;
  /** The field got a new value (G3): its error leaves. */
  clear(key: string): void;
  /** Before a new submit: every field error leaves. */
  clearAll(): void;
}

/**
 * The field errors of a form whose inputs write keys; record names the record
 * the inputs belong to, so a field error with a `target` binds too.
 */
export function useFieldErrors(keys: Iterable<string>, record?: FieldErrorRecord): FieldErrors {
  const formKeys: readonly string[] = [...keys];
  const texts = shallowRef<ReadonlyMap<string, string>>(new Map());

  function show(next: ReadonlyMap<string, string>): void {
    texts.value = new Map(next);
  }

  return {
    keys: formKeys,
    record,
    show,

    errorFor(key) {
      return texts.value.get(key);
    },

    clear(key) {
      if (!texts.value.has(key)) return;
      const next = new Map(texts.value);
      next.delete(key);
      texts.value = next;
    },

    clearAll() {
      if (texts.value.size > 0) texts.value = new Map();
    },
  };
}
